import {
  Alert, Button, Card, CardContent, Chip, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { executeShellCommandJson } from '../../api/shell';
import { sanitizeShellArg } from '../../utils/shell';

interface AuditEvent {
  auditEventId: string;
  actorType: string;
  actorId: string;
  action: string;
  targetType: string;
  targetId: string;
  payload?: Record<string, unknown>;
  createdAt: string;
}

export function AuditInspector() {
  const [actorId, setActorId] = useState('');
  const [action, setAction] = useState('');
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleFetch() {
    setLoading(true);
    setError('');
    try {
      let cmd = 'audit.list';
      if (actorId.trim()) cmd += ` --actor ${sanitizeShellArg(actorId)}`;
      if (action.trim()) cmd += ` --action ${sanitizeShellArg(action)}`;
      cmd += ' --limit 100';
      const res = await executeShellCommandJson(cmd);
      setEvents((res.events as AuditEvent[]) ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  const actionColors: Record<string, 'primary' | 'warning' | 'error' | 'info' | 'default'> = {
    'shell.execute': 'info',
    'shell.case.close': 'warning',
    'shell.case.abandon': 'error',
    'shell.asset.retry': 'warning',
    'case.create': 'primary',
    'case.read': 'default',
    'case.close': 'warning',
    'case.abandon': 'error',
    'save.write': 'primary',
  };

  return (
    <Stack spacing={3}>
      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Audit Log</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Actor ID (optional)"
                value={actorId}
                onChange={(e) => setActorId(e.target.value)}
                size="small"
                sx={{ width: 250 }}
              />
              <TextField
                label="Action (optional)"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                size="small"
                sx={{ width: 250 }}
                placeholder="shell.execute, case.create, ..."
              />
              <Button variant="contained" onClick={handleFetch} disabled={loading}>
                Fetch
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {events.length > 0 && (
        <Card sx={{ bgcolor: '#1a1d22' }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{events.length} event(s)</Typography>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Target</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((ev) => (
                    <TableRow key={ev.auditEventId} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {formatTime(ev.createdAt)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {ev.actorId}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ev.action}
                          size="small"
                          color={actionColors[ev.action] ?? 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {ev.targetType && <span style={{ color: '#b08d57' }}>{ev.targetType}:</span>}{' '}
                        {ev.targetId ? truncate(ev.targetId, 40) : '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 200 }}>
                        {ev.payload ? JSON.stringify(ev.payload) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.substring(0, max) + '...' : s;
}
